import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, message, Input, DatePicker, Modal, Descriptions, Segmented, Tag, Badge, Checkbox } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, ShoppingCartOutlined, ReloadOutlined, ExportOutlined, EyeOutlined, FileTextOutlined, EditOutlined, UploadOutlined, FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

interface Order {
  id: number;
  order_id: string;
  supplier_name: string;
  supplier_account: string;
  project_no: string;
  project_name: string;
  project_nature: string;
  order_amount: number;
  payment_status: string;
  payment_time: string;
  supplier_manager_name: string;
  supplier_manager_phone: string;
  sale_price: number;
  mailing_address: string;
  recipient_phone: string;
  recipient_name: string;
  bank_name: string;
  bank_account: string;
  invoice_request_time: string;
  invoice_url: string;
  expressnumber: string;
  create_time: string;
  announcement_id: number;
  trade_announcement_package_id: string;
  union_member: string;
}

interface PackageInfo {
  id: number;
  package_name: string;
  package_content: string;
}

interface AnnouncementDetail {
  project_name: string;
  project_no: string;
  project_nature: string;
  union_member: string;
  order_amount: number;
  packages: PackageInfo[];
  selected_package_ids: number[];
}

interface InvoiceDetail {
  supplier_name: string;
  supplier_account: string;
  mailing_address: string;
  order_amount: number;
  recipient_phone: string;
  recipient_name: string;
  register_address: string;
  register_phone: string;
  bank_name: string;
  bank_account: string;
  email: string;
  invoice_request_time: string;
}

export default function OrderCenter() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    order_id: '',
    supplier_name: '',
    project_name: '',
    payment_start_time: '',
    payment_end_time: '',
    payment_status: '已支付',
    invoice_status: '未上传',
    onlyPendingInvoice: false,
  });
  const [packageModalVisible, setPackageModalVisible] = useState(false);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [announcementDetail, setAnnouncementDetail] = useState<AnnouncementDetail | null>(null);
  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetail | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState(0);
  const [selectedPackageIds, setSelectedPackageIds] = useState<number[]>([]);
  const [savingPackages, setSavingPackages] = useState(false);
  const [pdfPreviewVisible, setPdfPreviewVisible] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [uploadingInvoice, setUploadingInvoice] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadTargetOrderId, setUploadTargetOrderId] = useState<number | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (searchParams.order_id) params.append('order_id', searchParams.order_id);
      if (searchParams.supplier_name) params.append('supplier_name', searchParams.supplier_name);
      if (searchParams.project_name) params.append('project_name', searchParams.project_name);
      if (searchParams.payment_start_time) params.append('payment_start_time', searchParams.payment_start_time);
      if (searchParams.payment_end_time) params.append('payment_end_time', searchParams.payment_end_time);
      if (searchParams.payment_status && searchParams.payment_status !== '全部') {
        params.append('payment_status', searchParams.payment_status);
      }
      if (searchParams.invoice_status && searchParams.invoice_status !== '全部') {
        params.append('invoice_status', searchParams.invoice_status);
      }
      if (searchParams.onlyPendingInvoice) {
        params.append('only_pending_invoice', 'true');
      }
      
      const resp = await fetch(`/api/admin/trade-orders?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setOrders(resp.data || []);
        setPendingInvoiceCount(resp.pendingInvoiceCount || 0);
      } else {
        message.error(resp?.error || '获取订单列表失败');
      }
    } catch (err) {
      message.error('获取订单列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchOrders();
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (searchParams.order_id) params.append('order_id', searchParams.order_id);
      if (searchParams.supplier_name) params.append('supplier_name', searchParams.supplier_name);
      if (searchParams.project_name) params.append('project_name', searchParams.project_name);
      if (searchParams.payment_start_time) params.append('payment_start_time', searchParams.payment_start_time);
      if (searchParams.payment_end_time) params.append('payment_end_time', searchParams.payment_end_time);
      if (searchParams.payment_status && searchParams.payment_status !== '全部') {
        params.append('payment_status', searchParams.payment_status);
      }
      if (searchParams.invoice_status && searchParams.invoice_status !== '全部') {
        params.append('invoice_status', searchParams.invoice_status);
      }
      if (searchParams.onlyPendingInvoice) {
        params.append('only_pending_invoice', 'true');
      }
      
      const response = await fetch(`/api/admin/trade-orders/export?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `订单导出_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        message.success('导出成功');
      } else {
        message.error('导出失败');
      }
    } catch (err) {
      message.error('导出失败');
    }
  };

  const handleViewPackage = async (record: Order) => {
    setSelectedOrder(record);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/trade-orders/${record.id}/package-detail`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setAnnouncementDetail(resp.data);
        // 设置已选择的分包ID - 确保转换为数字数组
        const ids = (resp.data.selected_package_ids || []).map((id: any) => Number(id));
        setSelectedPackageIds(ids);
        setPackageModalVisible(true);
      } else {
        message.error(resp?.error || '获取项目信息失败');
      }
    } catch (err) {
      message.error('获取项目信息失败');
    }
  };

  // 保存分包信息
  const handleSavePackages = async () => {
    if (!selectedOrder) return;
    
    if (selectedPackageIds.length === 0) {
      message.warning('请至少选择一个分包');
      return;
    }
    
    setSavingPackages(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/trade-orders/${selectedOrder.id}/update-packages`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ package_ids: selectedPackageIds })
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('分包信息保存成功');
        setPackageModalVisible(false);
        fetchOrders(); // 刷新列表
      } else {
        message.error(resp?.error || '保存失败');
      }
    } catch (err) {
      message.error('保存失败');
    } finally {
      setSavingPackages(false);
    }
  };

  // 切换分包选择 - 确保ID类型一致（转为数字）
  const handlePackageCheck = (packageId: number, checked: boolean) => {
    const numId = Number(packageId);
    setSelectedPackageIds(prev => {
      if (checked) {
        // 添加时检查是否已存在
        if (prev.includes(numId)) return prev;
        return [...prev, numId];
      } else {
        // 移除
        return prev.filter(id => Number(id) !== numId);
      }
    });
  };
  
  // 检查分包是否被选中
  const isPackageSelected = (packageId: number) => {
    return selectedPackageIds.some(id => Number(id) === Number(packageId));
  };

  // 格式化支付时间（使用东八区时间）
  const formatPaymentTime = (time: string | null) => {
    if (!time) return '-';
    try {
      const date = new Date(time);
      if (isNaN(date.getTime())) return time;
      // 使用东八区时间格式化
      return date.toLocaleString('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-');
    } catch (e) {
      return time;
    }
  };

  // 格式化时间为标准格式
  const formatDateTime = (time: string | null) => {
    if (!time) return '-';
    return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
  };

  // 处理发票上传按钮点击
  const handleUploadInvoiceClick = (orderId: number) => {
    setUploadTargetOrderId(orderId);
    fileInputRef.current?.click();
  };

  // 处理文件选择
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !uploadTargetOrderId) {
      return;
    }

    if (file.type !== 'application/pdf') {
      message.error('请选择PDF文件');
      return;
    }

    try {
      setUploadingInvoice(uploadTargetOrderId);
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/admin/trade-orders/${uploadTargetOrderId}/upload-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      if (result.ok) {
        message.success('发票上传成功');
        fetchOrders(); // 刷新列表
      } else {
        message.error(result.error || '上传失败');
      }
    } catch (error) {
      console.error('上传失败:', error);
      message.error('上传失败，请重试');
    } finally {
      setUploadingInvoice(null);
      setUploadTargetOrderId(null);
      // 清空input以便重复上传同一文件
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // 预览发票PDF
  const handlePreviewInvoice = (url: string) => {
    setPdfPreviewUrl(url);
    setPdfPreviewVisible(true);
  };

  const handleViewInvoice = async (record: Order) => {
    setSelectedOrder(record);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/trade-orders/${record.id}/invoice-detail`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setInvoiceDetail(resp.data);
        setInvoiceModalVisible(true);
      } else {
        message.error(resp?.error || '获取发票信息失败');
      }
    } catch (err) {
      message.error('获取发票信息失败');
    }
  };

  const columns: ColumnsType<Order> = [
    {
      title: '公司名称',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 180,
      ellipsis: true,
      render: (text, record) => {
        // 判断是否为待处理发票：有invoice_request_time但invoice_url和expressnumber都为空
        const isPendingInvoice = record.invoice_request_time && 
          (!record.invoice_url || record.invoice_url === '') && 
          (!record.expressnumber || record.expressnumber === '');
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isPendingInvoice && (
              <Badge dot style={{ backgroundColor: '#ef4444' }} />
            )}
            <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
          </div>
        );
      }
    },
    {
      title: '订单号',
      dataIndex: 'order_id',
      key: 'order_id',
      width: 200,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#3b82f6' }}>{text}</span>
    },
    {
      title: '支付时间',
      dataIndex: 'payment_time',
      key: 'payment_time',
      width: 170,
      render: (text) => <span style={{ color: '#6b7280', fontSize: 13 }}>{formatPaymentTime(text)}</span>
    },
    {
      title: '支付状态',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === '已支付' ? 'success' : 'warning'} style={{ borderRadius: 12 }}>
          {status}
        </Tag>
      ),
    },
    {
      title: '总价',
      dataIndex: 'order_amount',
      key: 'order_amount',
      width: 100,
      render: (price: number | string) => (
        <span style={{ color: '#f59e0b', fontWeight: 600 }}>
          ¥{price ? Number(price).toFixed(2) : '0.00'}
        </span>
      ),
    },
    {
      title: '项目名称',
      dataIndex: 'project_name',
      key: 'project_name',
      ellipsis: true,
    },
    {
      title: '项目编号',
      dataIndex: 'project_no',
      key: 'project_no',
      width: 130,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#6b7280' }}>{text}</span>
    },
    {
      title: '联系人',
      dataIndex: 'supplier_manager_name',
      key: 'supplier_manager_name',
      width: 100,
    },
    {
      title: '手机号',
      dataIndex: 'supplier_manager_phone',
      key: 'supplier_manager_phone',
      width: 130,
    },
    {
      title: '发票信息',
      dataIndex: 'invoice_url',
      key: 'invoice_url',
      width: 100,
      render: (url: string) => {
        if (url) {
          return (
            <Tag 
              color="blue" 
              style={{ cursor: 'pointer', borderRadius: 12 }}
              onClick={() => handlePreviewInvoice(url)}
            >
              已上传
            </Tag>
          );
        }
        return <Tag color="default" style={{ borderRadius: 12 }}>未上传</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleViewPackage(record)}
            style={{ color: '#3b82f6', padding: '0 4px', height: 24, fontSize: 12 }}
          >
            查看（修改）包件
          </Button>
          <Button
            type="link"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => handleViewInvoice(record)}
            style={{ color: '#10b981', padding: '0 4px', height: 24, fontSize: 12 }}
          >
            发票信息
          </Button>
          <Button
            type="link"
            size="small"
            icon={<UploadOutlined />}
            onClick={() => handleUploadInvoiceClick(record.id)}
            loading={uploadingInvoice === record.id}
            style={{ color: '#8b5cf6', padding: '0 4px', height: 24, fontSize: 12 }}
          >
            上传发票
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <ShoppingCartOutlined />
          </div>
          <div>
            <h2>订单管理</h2>
            <p>管理所有交易订单</p>
          </div>
          {pendingInvoiceCount > 0 && (
            <Badge count={pendingInvoiceCount} style={{ marginLeft: 12 }}>
              <Tag color="red">待处理发票</Tag>
            </Badge>
          )}
        </div>
      </div>
      
      {/* 筛选区域 */}
      <div className="admin-page-content" style={{ marginBottom: 16 }}>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 16, 
          alignItems: 'flex-end',
          padding: '16px',
          background: '#f9fafb',
          borderRadius: 8,
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>订单号</div>
            <Input
              placeholder="请输入订单号"
              value={searchParams.order_id}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchParams({ ...searchParams, order_id: e.target.value })}
              style={{ width: 150 }}
              allowClear
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>公司名称</div>
            <Input
              placeholder="请输入公司名称"
              value={searchParams.supplier_name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchParams({ ...searchParams, supplier_name: e.target.value })}
              style={{ width: 150 }}
              allowClear
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>项目名称</div>
            <Input
              placeholder="请输入项目名称"
              value={searchParams.project_name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchParams({ ...searchParams, project_name: e.target.value })}
              style={{ width: 150 }}
              allowClear
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>支付开始时间</div>
            <DatePicker
              showTime
              placeholder="选择开始时间"
              value={searchParams.payment_start_time ? dayjs(searchParams.payment_start_time) : null}
              onChange={(date) => setSearchParams({ 
                ...searchParams, 
                payment_start_time: date ? date.format('YYYY-MM-DD HH:mm:ss') : '' 
              })}
              style={{ width: 180 }}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>支付结束时间</div>
            <DatePicker
              showTime
              placeholder="选择结束时间"
              value={searchParams.payment_end_time ? dayjs(searchParams.payment_end_time) : null}
              onChange={(date) => setSearchParams({ 
                ...searchParams, 
                payment_end_time: date ? date.format('YYYY-MM-DD HH:mm:ss') : '' 
              })}
              style={{ width: 180 }}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>支付状态</div>
            <Segmented
              value={searchParams.payment_status}
              onChange={(value: string) => setSearchParams({ ...searchParams, payment_status: value })}
              options={['全部', '已支付', '待付款']}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>发票状态</div>
            <Segmented
              value={searchParams.invoice_status}
              onChange={(value: string) => setSearchParams({ ...searchParams, invoice_status: value })}
              options={['全部', '已上传', '未上传']}
            />
          </div>
          <Space>
            <Button 
              type="primary" 
              icon={<SearchOutlined />} 
              onClick={handleSearch}
              style={{ 
                borderRadius: 8,
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                border: 'none'
              }}
            >
              查询
            </Button>
            <Button 
              icon={<ExportOutlined />} 
              onClick={handleExport}
              style={{ borderRadius: 8 }}
            >
              导出订单
            </Button>
            <Button 
              icon={<EyeOutlined />} 
              onClick={() => setSearchParams({ ...searchParams, onlyPendingInvoice: !searchParams.onlyPendingInvoice })}
              style={{ 
                borderRadius: 8,
                backgroundColor: searchParams.onlyPendingInvoice ? '#3b82f6' : 'white',
                color: searchParams.onlyPendingInvoice ? 'white' : '#3b82f6',
                border: `1px solid ${searchParams.onlyPendingInvoice ? '#3b82f6' : '#d1d5db'}`
              }}
            >
              只看待处理发票
            </Button>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchOrders}
              style={{ borderRadius: 8 }}
            >
              刷新
            </Button>
          </Space>
        </div>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{orders.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={orders}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 1500 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      {/* 查看（修改）包件弹窗 */}
      <Modal
        title="项目信息"
        open={packageModalVisible}
        onCancel={() => setPackageModalVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button onClick={() => setPackageModalVisible(false)}>
              取消
            </Button>
            <Button 
              type="primary" 
              onClick={handleSavePackages}
              loading={savingPackages}
              style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}
            >
              保存
            </Button>
          </div>
        }
        width={800}
      >
        {announcementDetail && selectedOrder && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="项目名称" span={2}>{announcementDetail.project_name}</Descriptions.Item>
            <Descriptions.Item label="项目编号">{announcementDetail.project_no}</Descriptions.Item>
            <Descriptions.Item label="项目性质">{announcementDetail.project_nature || '-'}</Descriptions.Item>
            <Descriptions.Item label="采购文件售价">¥{announcementDetail.order_amount ? Number(announcementDetail.order_amount).toFixed(2) : '0.00'}</Descriptions.Item>
            <Descriptions.Item label="联合体报名单位">{announcementDetail.union_member || '-'}</Descriptions.Item>
            <Descriptions.Item label="分包信息" span={2}>
              {announcementDetail.packages && announcementDetail.packages.length > 0 ? (
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                  {announcementDetail.packages.map((pkg) => {
                    const isSelected = isPackageSelected(pkg.id);
                    return (
                      <div key={`pkg-${pkg.id}`} style={{ 
                        padding: '12px', 
                        background: isSelected ? '#eff6ff' : '#f9fafb', 
                        borderRadius: 8,
                        marginBottom: 8,
                        border: isSelected ? '1px solid #3b82f6' : '1px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => handlePackageCheck(pkg.id, !isSelected)}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                          <Checkbox 
                            checked={isSelected}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              e.stopPropagation();
                              handlePackageCheck(pkg.id, e.target.checked);
                            }}
                            onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: '#1f2937' }}>{pkg.package_name}</div>
                            <div style={{ color: '#6b7280', fontSize: 13 }}>{pkg.package_content}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span style={{ color: '#999' }}>暂无分包信息</span>
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      
      {/* 发票信息弹窗 */}
      <Modal
        title="发票信息"
        open={invoiceModalVisible}
        onCancel={() => setInvoiceModalVisible(false)}
        footer={null}
        width={910}
      >
        {invoiceDetail && selectedOrder && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="公司名称" span={2}>{invoiceDetail.supplier_name}</Descriptions.Item>
            <Descriptions.Item label="纳税人识别号">{invoiceDetail.supplier_account}</Descriptions.Item>
            <Descriptions.Item label="申请开票金额">
              <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                ¥{invoiceDetail.order_amount ? Number(invoiceDetail.order_amount).toFixed(2) : '0.00'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="联系电话">{selectedOrder.supplier_manager_phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="联系人姓名">{selectedOrder.supplier_manager_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="注册地址" span={2}>{invoiceDetail.register_address || '-'}</Descriptions.Item>
            <Descriptions.Item label="邮箱地址">{invoiceDetail.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="申请时间" span={2}>{formatDateTime(invoiceDetail.invoice_request_time)}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      
      {/* 隐藏的文件输入框 */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".pdf"
        onChange={handleFileChange}
      />
      
      {/* PDF预览弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FilePdfOutlined />
            <span>发票预览</span>
          </div>
        }
        open={pdfPreviewVisible}
        onCancel={() => setPdfPreviewVisible(false)}
        footer={null}
        width={900}
        bodyStyle={{ padding: 0, height: '70vh' }}
        centered
      >
        <iframe
          src={pdfPreviewUrl}
          style={{ width: '100%', height: '100%', border: 'none' }}
          title="发票预览"
        />
      </Modal>
    </div>
  );
}
